// variants.js

if (!customElements.get("variant-options")) {
  customElements.define(
    "variant-options",
    class VariantOptions extends HTMLElement {
      whenLoaded = Promise.all([customElements.whenDefined("gallery-section")]);

      constructor() {
        super();
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.addEventListener("change", this.onVariantChange);
        this.addEventListener("keydown", this.onKeyDown);
      }

      connectedCallback() {
        this.whenLoaded.then(() => this.initialize());
      }

      initialize() {
        this.stockFilter = "in-stock";
        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.setupStockFilter();
        this.updateVariantStatuses();
        this.updateDropdownButtons();
        this.bindFabricChangeListener();
      }

      onKeyDown(event) {
        if (event.key === "Enter" || event.keyCode === 13) {
          const input = event.target.querySelector("input");
          if (input) input.click();
        }
      }

      onVariantChange() {
        this.dispatchEvent(
          new CustomEvent("variantChangeStart", { bubbles: true, composed: true })
        );

        this.toggleAddButton(true, "", false);

        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.updatePickupAvailability();
        this.removeErrorMessage();

        this.updateVariantStatuses();
        this.updateDropdownButtons();

        if (!this.currentVariant) {
          this.setUnavailable();
        } else {
          this.updateMedia();
          this.lenOfVariantOptions = document.querySelectorAll("variant-options").length;
          if (this.lenOfVariantOptions === 1) this.updateURL();
          this.updateVariantInput();
          this.renderProductInfo();
          this.updateShareUrl();

          const available = this.currentVariant?.available ?? true;
          this.toggleAddButton(!available, available ? "" : window.variantStrings.soldOut, true);
        }

        this.dispatchEvent(
          new CustomEvent("variantChangeEnd", { bubbles: true, composed: true })
        );
      }

      /**
       * Listen for changes in the fabric fieldset, then reset the stock filter to
       * "in-stock" if possible and snap to the first matching colour.
       */
      bindFabricChangeListener() {
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        if (fieldsets.length < 2) return;

        const fabricIndex = fieldsets.length === 2 ? 0 : 1;
        const fabricFs = fieldsets[fabricIndex];
        if (!fabricFs) return;

        fabricFs.querySelectorAll('input[type="radio"]').forEach((radio) => {
          radio.addEventListener("change", () => {
            setTimeout(() => {
              const filter = this.querySelector(".bb-stock-filter");
              if (filter && this.stockFilter !== "in-stock") {
                const inStockBtn = filter.querySelector('[data-filter="in-stock"]');
                if (inStockBtn && inStockBtn.style.display !== "none") {
                  filter.querySelectorAll(".bb-stock-filter__btn").forEach((b) => {
                    b.classList.remove("bb-stock-filter__btn--active");
                  });
                  inStockBtn.classList.add("bb-stock-filter__btn--active");
                  this.stockFilter = "in-stock";
                  const fsets = Array.from(this.querySelectorAll(".wt-product__option"));
                  this.applyStockFilter(fsets);
                }
              }
              this.autoSelectFirstAvailableColour();
            }, 0);
          });
        });
      }

      autoSelectFirstAvailableColour() {
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        const colorFs = fieldsets.find((fs) => fs.querySelector("[data-bb-stock-type]"));
        if (!colorFs) return;

        const firstAvail = Array.from(colorFs.querySelectorAll("li")).find((li) => {
          const inp = li.querySelector("input");
          return (
            inp &&
            !li.classList.contains("is-hidden") &&
            !li.classList.contains("bb-filter-hidden") &&
            !inp.disabled
          );
        });
        const input = firstAvail?.querySelector("input");
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      // ----- Core variant logic -----

      updateOptions() {
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        this.options = fieldsets.map((fs) => {
          const sel = Array.from(fs.querySelectorAll("input")).find((i) => i.checked);
          return sel ? sel.value : "";
        });
      }

      updateMasterId() {
        const variants = this.getVariantData();
        this.currentVariant = variants.find((v) =>
          v.options.every((opt, i) => this.options[i] === opt)
        );
      }

      updateGallery() {
        const gallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
        const mediaId = this.currentVariant?.featured_media?.id || false;
        gallery?.filterSlides(this.options, mediaId, true);
      }

      updateVariantStatuses() {
        const variants = Array.isArray(this.variantData) ? this.variantData : [];
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));

        fieldsets.forEach((fs, idx) => {
          if (idx === 0) return;
          const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
          const allowed = variants
            .filter((v) => {
              if (!v.available) return false;
              for (let i = 0; i < idx; i++) {
                if (v.options[i] !== this.options[i]) return false;
              }
              return true;
            })
            .map((v) => v.options[idx]);

          inputs.forEach((input) => {
            const ok = allowed.includes(input.value);
            input.disabled = !ok;
            input.classList.toggle("disabled", !ok);

            const li = input.closest("li");
            if (li) {
              li.classList.toggle("is-hidden", !ok);
              if (!ok) {
                li.setAttribute("aria-hidden", "true");
                li.setAttribute("inert", "");
              } else {
                li.removeAttribute("aria-hidden");
                li.removeAttribute("inert");
              }
            }
          });
        });

        try { this.updateColourStockTypes(); } catch (e) { console.warn("[BB] Stock type update error:", e); }
        try { this.updateStockFilterVisibility(fieldsets); } catch (e) { console.warn("[BB] Stock filter visibility error:", e); }
        try { this.applyStockFilter(fieldsets); } catch (e) { console.warn("[BB] Stock filter error:", e); }
      }

      // Updates data-bb-stock-type on each colour <li> based on all currently selected preceding options.
      // Reads data-bb-stock-map (format: { "opt1|||opt2": "in-stock"|"made-to-order" }).
      updateColourStockTypes() {
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        if (fieldsets.length < 2) return;
        const lastFs = fieldsets[fieldsets.length - 1];
        const prevOptions = this.options.slice(0, -1);
        if (prevOptions.some((o) => o == null || o === "")) return;
        const compositeKey = prevOptions.join("|||");

        Array.from(lastFs.querySelectorAll("li[data-bb-stock-map]")).forEach((li) => {
          const mapStr = li.getAttribute("data-bb-stock-map");
          if (!mapStr) return;
          try {
            const map = JSON.parse(mapStr);
            const stockType = map[compositeKey];
            if (stockType) li.setAttribute("data-bb-stock-type", stockType);
          } catch (e) {
            console.warn("[BB] Stock map parse error:", e);
          }
        });
      }

      // Shows/hides filter buttons based on which stock types exist for the current fabric.
      updateStockFilterVisibility(fieldsets) {
        const filter = this.querySelector(".bb-stock-filter");
        if (!filter || !fieldsets) return;

        let colorFs = null;
        for (let i = 0; i < fieldsets.length; i++) {
          if (fieldsets[i].querySelector("[data-bb-stock-type]")) { colorFs = fieldsets[i]; break; }
        }
        if (!colorFs) return;

        let hasInStock = false;
        let hasMTO = false;
        Array.from(colorFs.querySelectorAll("li")).forEach((li) => {
          if (li.classList.contains("is-hidden")) return;
          const t = li.dataset.bbStockType;
          if (t === "in-stock") hasInStock = true;
          if (t === "made-to-order") hasMTO = true;
        });

        const inStockBtn = filter.querySelector('[data-filter="in-stock"]');
        const mtoBtn = filter.querySelector('[data-filter="made-to-order"]');
        if (inStockBtn) inStockBtn.style.display = hasInStock ? "" : "none";
        if (mtoBtn) mtoBtn.style.display = hasMTO ? "" : "none";

        const activeBtn = filter.querySelector(".bb-stock-filter__btn--active");
        if (activeBtn && activeBtn.style.display === "none") {
          const fallback = hasInStock ? inStockBtn : mtoBtn;
          if (fallback) {
            filter.querySelectorAll(".bb-stock-filter__btn").forEach((b) => {
              b.classList.remove("bb-stock-filter__btn--active");
            });
            fallback.classList.add("bb-stock-filter__btn--active");
            this.stockFilter = fallback.dataset.filter;
          }
        }
      }

      setupStockFilter() {
        const filter = this.querySelector(".bb-stock-filter");
        if (!filter) return;

        const hasInStock = !!this.querySelector('[data-bb-stock-type="in-stock"]');
        const hasMTO = !!this.querySelector('[data-bb-stock-type="made-to-order"]');

        if (!hasInStock && !hasMTO) {
          filter.style.display = "none";
          this.stockFilter = "all";
          return;
        }

        const inStockBtn = filter.querySelector('[data-filter="in-stock"]');
        const mtoBtn = filter.querySelector('[data-filter="made-to-order"]');
        if (inStockBtn) inStockBtn.style.display = hasInStock ? "" : "none";
        if (mtoBtn) mtoBtn.style.display = hasMTO ? "" : "none";

        const defaultFilter = hasInStock ? "in-stock" : "made-to-order";
        this.stockFilter = defaultFilter;
        filter.querySelectorAll(".bb-stock-filter__btn").forEach((b) => {
          b.classList.toggle("bb-stock-filter__btn--active", b.dataset.filter === defaultFilter);
        });

        filter.querySelectorAll(".bb-stock-filter__btn").forEach((btn) => {
          btn.addEventListener("click", () => {
            if (btn.classList.contains("bb-stock-filter__btn--active")) return;
            filter.querySelectorAll(".bb-stock-filter__btn").forEach((b) => {
              b.classList.remove("bb-stock-filter__btn--active");
            });
            btn.classList.add("bb-stock-filter__btn--active");
            this.stockFilter = btn.dataset.filter;
            this.updateVariantStatuses();
            this.autoSelectFirstAvailableColour();
          });
        });
      }

      applyStockFilter(fieldsets) {
        if (!this.stockFilter || this.stockFilter === "all") return;

        fieldsets.forEach((fs) => {
          if (!fs.querySelector("[data-bb-stock-type]")) return;

          Array.from(fs.querySelectorAll('input[type="radio"]')).forEach((input) => {
            const li = input.closest("li");
            if (!li) return;
            const stockType = li.dataset.bbStockType;
            if (!stockType) return;

            const matchesFilter = stockType === this.stockFilter;
            li.classList.toggle("bb-filter-hidden", !matchesFilter);
            if (!matchesFilter) {
              li.setAttribute("aria-hidden", "true");
              li.setAttribute("inert", "");
            } else if (!li.classList.contains("is-hidden")) {
              li.removeAttribute("aria-hidden");
              li.removeAttribute("inert");
            }
          });
        });
      }

      updateDropdownButtons() {
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        fieldsets.forEach((fs) => {
          const sel = fs.querySelector("input:checked");
          const title = fs.querySelector(".wt-product__option__title .value");
          const span = fs.querySelector(".wt-product__option__dropdown span");
          if (sel && title) title.textContent = sel.value;
          if (sel && span) span.textContent = sel.value;

          const dropdown = fs.querySelector(".wt-product__option__dropdown");
          if (dropdown) {
            dropdown.classList.toggle(
              "wt-product__option__dropdown--unavailable",
              sel?.disabled
            );
          }
        });
      }

      updateMedia() {
        if (!this.currentVariant?.featured_media) return;
        this.setAttribute("data-variant-id", this.currentVariant.id);
        this.setAttribute(
          "data-featured-image",
          this.currentVariant.featured_media.preview_image.src
        );
        this.setAttribute("data-featured-image-id", this.currentVariant.featured_media.id);

        const modal = document.querySelector(
          `#ProductModal-${this.dataset.section} .product-media-modal__content`
        );
        const slide = modal?.querySelector(
          `[data-media-id="${this.currentVariant.featured_media.id}"]`
        );
        if (modal && slide) modal.prepend(slide);
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${this.dataset.url}?variant=${this.currentVariant.id}`
        );
      }

      updateShareUrl() {
        const btn = document.getElementById(`Share-${this.dataset.section}`);
        if (btn?.updateUrl) {
          btn.updateUrl(
            `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`
          );
        }
      }

      updateVariantInput() {
        document
          .querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
          )
          .forEach((form) => {
            const input = form.querySelector('input[name="id"]');
            if (!input) return;
            input.value = this.currentVariant.id;
            input.dispatchEvent(new Event("change", { bubbles: true }));
          });
      }

      updatePickupAvailability() {
        const pu = document.querySelector("pickup-availability");
        if (!pu) return;
        pu.dataset.variantId = this.currentVariant?.id || "";
        if (this.currentVariant?.available) pu.fetchAvailability(this.currentVariant.id);
        else {
          pu.removeAttribute("available");
          pu.innerHTML = "";
        }
      }

      removeErrorMessage() {
        const section = this.closest("section");
        const frm = section?.querySelector("product-form");
        if (frm && typeof frm.handleErrorMessage === "function") {
          frm.handleErrorMessage();
        }
      }

      renderProductInfo() {
        const vid = this.currentVariant.id;
        const sectionId = this.dataset.originalSection || this.dataset.section;

        fetch(`${this.dataset.url}?variant=${vid}&section_id=${sectionId}`)
          .then((r) => r.text())
          .then((txt) => {
            if (this.currentVariant.id !== vid) return;

            const doc = new DOMParser().parseFromString(txt, "text/html");

            ["price", "Sku", "Inventory"].forEach((key) => {
              const src = doc.getElementById(`${key}-${sectionId}`);
              const dst = document.getElementById(`${key}-${this.dataset.section}`);
              if (src && dst) dst.innerHTML = src.innerHTML;
            });

            const addButtonUpdated = doc.getElementById(`ProductSubmitButton-${sectionId}`);
            if (addButtonUpdated) {
              const disabled = addButtonUpdated.hasAttribute("disabled");
              this.toggleAddButton(disabled, disabled ? window.variantStrings.soldOut : "", true);
            } else {
              const available = this.currentVariant?.available ?? true;
              this.toggleAddButton(!available, available ? "" : window.variantStrings.soldOut, true);
            }
          });
      }

      toggleAddButton(disable = true, text = "", modifyClass = true) {
        const form = document.getElementById(`product-form-${this.dataset.section}`);
        const btn = form?.querySelector('[name="add"]');
        const sp = btn?.querySelector("span");
        if (!btn) return;

        if (disable) {
          btn.setAttribute("disabled", "");
          if (text && sp) sp.textContent = text;
        } else {
          btn.removeAttribute("disabled");
          if (sp) sp.textContent = window.variantStrings.addToCart;
        }
      }

      setUnavailable() {
        const form = document.getElementById(`product-form-${this.dataset.section}`);
        const span = form?.querySelector('[name="add"] > span');
        if (span) span.textContent = window.variantStrings.unavailable;
        const priceEl = document.getElementById(`price-${this.dataset.section}`);
        if (priceEl) priceEl.classList.add("visibility-hidden");
        const final = priceEl?.querySelector(".price-item--regular");
        if (final) final.innerText = this.dataset.lowestPrice;
        ["Inventory", "Sku"].forEach((k) => {
          document.getElementById(`${k}-${this.dataset.section}`)?.classList.add("visibility-hidden");
        });
      }

      getVariantData() {
        if (!this.variantData) {
          const script = this.querySelector('script[type="application/json"]');
          this.variantData = script ? JSON.parse(script.textContent) : [];
        }
        return this.variantData;
      }
    }
  );
}
